// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useState, ReactPortal } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import Form from 'antd/lib/form';
import Input from 'antd/lib/input';
import Button from 'antd/lib/button';
import { Row, Col } from 'antd/lib/grid';
import { Store } from 'antd/lib/form/interface';

import { reviewActions, finishIssueAsync } from 'actions/review-actions';
import { useIsMounted } from 'utils/hooks';
import { MessageOutlined } from '@ant-design/icons';

interface FormProps {
    top: number;
    left: number;
    angle: number;
    scale: number;
    fetching: boolean;
    submit(message: string): void;
    cancel(): void;
}

function MessageForm(props: FormProps): JSX.Element {
    const {
        top, left, angle, scale, fetching, submit, cancel,
    } = props;

    function handleSubmit(values: Store): void {
        const generalComment = `${values.issue_description}`;
        submit(generalComment);
    }

    return (
        <Form
            className='cvat-create-issue-dialog'  //same class as for issues
            style={{
                top,
                left,
                transform: `scale(${scale}) rotate(${angle}deg)`,
            }}
            onFinish={(values: Store) => handleSubmit(values)}
        >
             <p style={{
            marginBottom: '1em',
            fontSize: '1rem',
            lineHeight: 1.4,
            color: '#333',
            maxWidth: '100%',
            wordWrap: 'break-word',
        }}>
            <MessageOutlined style={{ marginRight: '0.5em' }} />
            Enter a comment about this image:
        </p>
            <Form.Item
                name='issue_description'
                rules={[{ required: true, message: 'Please enter a comment related to this image' }]}
            >
                <Input
                    autoFocus
                    autoComplete='off'
                    placeholder='Please enter a comment related to this image'
                />
            </Form.Item>
            <Row justify='space-between'>
                <Col>
                    <Button
                        onClick={cancel}
                        disabled={fetching}
                        className='cvat-create-general-comment-cancel-button'
                    >
                        Cancel
                    </Button>
                </Col>
                <Col>
                    <Button
                        loading={fetching}
                        disabled={fetching}
                        type='primary'
                        htmlType='submit'
                        className='cvat-create-general-comment-submit-button'
                    >
                        Submit
                    </Button>
                </Col>
            </Row>
        </Form>
    );
}

interface Props {
    top: number;
    left: number;
    angle: number;
    scale: number;
    onCreateIssue: () => void;
}

export default function CreateGeneralCommentDialog(props: Props): ReactPortal {
    const [fetching, setFetching] = useState(false);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {
        top, left, angle, scale, onCreateIssue,
    } = props;

    return ReactDOM.createPortal(
        <MessageForm
            top={top}
            left={left}
            angle={angle}
            scale={scale}
            fetching={fetching}
            submit={(message: string) => {
                setFetching(true);
            
                // Ensure the message starts with the generalissue prefix
                const prefixedMessage = `generalcomment#- ${message}`;
            
                dispatch(finishIssueAsync(prefixedMessage)).finally(() => {
                    if (isMounted()) {
                        setFetching(false);
                    }
                    onCreateIssue();
                });
            }}
            
            cancel={() => {
                dispatch(reviewActions.cancelIssue());
            }}
        />, window.document.getElementById('cvat_canvas_attachment_board') as HTMLElement,
    );
}